//
//	+-----------------------------------------------+
//	| Cardboard mixin objects                       |
// 	+-----------------------------------------------+

/**
 * @file cardboard/mixins.js
 * @desc Mixins, for various data model objects that have different ancestries,
 * but need a common set of methods.
 * @author Geoffrey Pursell
 */

define('cardboard/mixins',["jquery", "underscore", "backbone", "rsvp",
        "cardboard/util"
    ],
    function ($, _, Backbone, RSVP,
        Util) {

        console.log("MODULE: cardboard/mixins");

        /**
         * @exports cardboard/mixins
         * @desc Mixins, for various data model objects that have different
         * ancestries, but need a common set of methods.
         */
        var Mixins = {},
            Promise = RSVP.Promise;

        //
        // MIXINS
        //

        /**  this goes on all models and collections
         * @mixin */
        Mixins.ModelAndCollectionMixin = {
            /** This calls the default sync() with some custom options. No
             * big deal.
             * @param method
             * @param model
             * @param options
             * @returns {jqXHR} */
            sync: function (method, model, options) {
                var newOptions = {},
                    mappedRequestParams = this.getMappedRequestParams();

                _(newOptions).extend(
                    this.ajaxOptions, // default ajax stuff
                    {
                        data: mappedRequestParams // the HTTP params
                    },
                    options // the options, passed in
                );

                if (this.jqXHR) {
                    this.jqXHR.abort();
                }
                this.jqXHR = Backbone.sync(method, model, newOptions);
                this.requestDonePromise = Promise.cast(this.jqXHR);

                return this.jqXHR;
            },

            /** construct a properly mapped request parameter hash to make an ajax
             * call
             * @returns {Object} URL paramters */
            getMappedRequestParams: function () {
                var definition = this.getDefinition(),
                    paramMappings = definition.paramMappings,
                    defaultParams = definition.defaultParams || {}, // params have internal names
                    inputParams = {}, // mixture of internal and WF names
                    outputParams;

                // map the parameter names
                if (definition.webFocusAdapter && definition.webFocusAdapter.paramMappings) {
                    paramMappings = definition.webFocusAdapter.paramMappings;
                }

                // Let's assemble the parameters, starting with the defaults
                _.extend(inputParams, defaultParams, this.params);

                // Then, map the parameters. This will wipe out parameters that have no given mapping.
                outputParams = Util.Data.buildJSON(paramMappings, inputParams);

                // So, restore parameters that have no mapping...
                _.defaults(outputParams, inputParams);

                // ...but omit parameters that HAVE been mapped!
                outputParams = _.omit(outputParams, _.values(paramMappings));

                // Also omit parameters marked as null!
                var nullParametersList = _(paramMappings)
                    .pairs()
                    .filter(function (pair) {
                        return pair[1] === null;
                    })
                    .object()
                    .keys()
                    .value();
                outputParams = _.omit(outputParams, nullParametersList);

                // And we're done.
                return this._flattenCheckboxArrays(outputParams);
            },

            /** by default, checkboxes from a form are serialized into arrays, which
             * are sent across to an ajax call as &boxName[]=value&boxName[]=value2
             * and so on. The new API prefers a single parameter with pipe-delimited
             * values, so let's do that.
             * @private
             * @param {Object} params - the object containing parameter key/value
             * pairs
             * @returns {Object} the same object, but with any array values
             * flattened into single, pipe-delimited strings */
            _flattenCheckboxArrays: function (params) {
                var result = _(params).map(function (paramValue, paramName) {
                    if (paramValue instanceof Array) {
                        return [paramName, paramValue.join("|")];
                    }
                    return [paramName, paramValue];
                }).object().value();

                return result;
            },

            /**	Looks for data at the requested dataPath; also applies any sorting
             * requested by the view, which is more complex than I'd like it to
             * be. This really needs unit tests, but there aren't any yet.
             * @returns {Array | Object} the data needed for a chart,
             * converted to an array if necessary */
            getCleanDataView: function (viewInfo) {
                var rawData = this.toJSON(),
                    output = {};

                if (viewInfo.dataPath) {

                    output.data = Util.Data.getDataFromPath(rawData, viewInfo.dataPath);

                    if (this.loaded && !output.data) {
                        console.log("WARNING: no data found at path " + viewInfo.dataPath);
                        console.log("Raw data:");
                        console.log(rawData);
                        return null;
                    }

                } else {
                    output.data = rawData;
                }

                output.data = this._convertDataHashToArray(viewInfo, output.data);
                output.data = this._sortCleanDataView(viewInfo, output.data);

                // if there's a meta data path provided, tack that on to the data
                // view. it's not entirely clean, but it's functional.
                if (viewInfo.metaDataPath) {
                    output.meta = this.getCleanMetaDataView(viewInfo);
                }
                //console.log("Output", window.done);
                //window.done = true;
                return output;
            },

            /**	Looks for metadata at the requested dataPath; it's much simpler than
             * getCleanDataView().
             * @returns {Array | Object} the metadata if it exists
             * */
            getCleanMetaDataView: function (viewInfo) {
                if (viewInfo.metaDataPath) {
                    return Util.Data.getDataFromPath(this.toJSON(), viewInfo.metaDataPath);
                }
                return null;
            },

            /** helper for getCleanDataView()
             * @private
             * @returns {Array} */
            _convertDataHashToArray: function (viewInfo, output) {
                var hashToConvert;

                if (viewInfo.convertDataHashToArray) {
                    if (_.isString(viewInfo.convertDataHashToArray)) {

                        // so we're working on a copy
                        output = _.cloneDeep(output);

                        // get the hash to be converted
                        hashToConvert = Util.Data.getDataFromPath(output, viewInfo.convertDataHashToArray);

                        // convert and hang it on the output data structure
                        Util.Data.setDataAtPath(output, viewInfo.convertDataHashToArray, _.values(hashToConvert));
                    } else {
                        output = _.values(output);
                    }
                }
                return output;
            },

            /** helper for getCleanDataView()
             * @private */
            _sortCleanDataView: function (viewInfo, output) {
                var _output, unsortedTarget, _sortedTarget,
                    sortInfo = viewInfo.sort;

                if (sortInfo) {
                    // we must operate on a clone
                    _output = _(_(output).clone());

                    if (_output.isArray()) {
                        _output.each(function (rawDataRow) {
                            if (sortInfo.collection) {
                                // we're sorting an array somewhere nested inside
                                // the data structure; the path is given by the
                                // collection property. we need to go find the array
                                // at that path.  if it turns out not to be an
                                // array, I guess we do nothing.
                                unsortedTarget = Util.Data.getDataFromPath(rawDataRow, sortInfo.collection);

                                if (_.isArray(unsortedTarget)) {
                                    _sortedTarget = _(unsortedTarget).sortBy(function (row) {
                                        Util.Data.getDataFromPath(row, sortInfo.by);
                                    });

                                    // sortBy sorts ascending by default, so if descending sort
                                    // was requested, just reverse it
                                    if (sortInfo.order === "descending") {
                                        _sortedTarget = _sortedTarget.reverse();
                                    }

                                    // finally, hang the new array on the old slot. we're done
                                    Util.Data.setDataAtPath(rawDataRow, sortInfo.collection, _sortedTarget.value());
                                }
                                return _output.value();
                            } else {
                                // we sort on the collection itself, not a nested object.
                                // not yet implemented.
                                return output;
                            }
                        }.bind(this));
                    }
                }
                return output;
            },

            /** a simple interface that gives you back the data source's definition
             * object from the plugin -- or undefined
             * @returns {object} the data source definition object */
            getDefinition: function () {
                return this.dataSourceInfo;
            },

            wipe: function () {
                this.loaded = false;
                if (this.clear) {
                    this.clear();
                } else if (this.reset) {
                    this.reset([]);
                }
                // neither the clear nor reset methods will trigger the
                // sync event, and we would like views to act as though
                // the model has been synced (and receieved empty data),
                // so this is okay.
                this.trigger("wipe", this);
            },

            /** sets the request parameters for the model object. right
             * away, this should be used to set the default params; later,
             * as the user makes config selections, this gets called to set
             * and store the new non-default set of params. */
            setParams: function (inputParams) {
                var outputParams = {},
                    defaultParams = this.getDefinition().defaultParams;

                inputParams = _.isObject(inputParams) ? inputParams : {};
                _.extend(outputParams, defaultParams, inputParams);
                this.params = outputParams;
       
            },

            /** execute any postProcessor function in the data source definition */
            postProcess: function (data) {
                try {
                    var dataSourceDefinition = this.getDefinition();
                    if (dataSourceDefinition && typeof dataSourceDefinition.postProcessor === "function") {
                        this.getDefinition().postProcessor.call(this, data);
                    }
                } catch (error) {
                    console.log("error in data source post-processing function.");
                    console.log(error);
                    return;
                }
            },

            //
            // ERROR HANDLING
            //

            /** check for any error conditions specified in the data source info. if
             * we find one, we are in an error state and we should react
             * appropriately. Views will need to be updated. Currently, this
             * supports tracking multiple errors, but reporting only one error. Good
             * enough for now. */
            checkForErrorConditions: function () {
                var errors = [];

                if (this.getDefinition() && this.getDefinition().errorConditions) {
                    _(this.getDefinition().errorConditions).each(function (errorCondition) {
                        var isError = Util.evaluateFilterCondition(errorCondition, this.toJSON());
                        if (isError) {
                            errors.push(errorCondition.error);
                        }
                    }.bind(this));
                }

                if (errors.length) {
                    this.setDataErrorState(errors[0]);
                } else {
                    this.setDataErrorState(null);

                    // for testing: always error
                    // this.setDataErrorState({
                    // 	type: "NoData",
                    // 	httpStatus: errors.status,
                    // 	header: "Error",
                    // 	message: "You are \"experiencing\" a fake technical issue with our data servers. Please check back later.",
                    // 	debugMessage: "debug message, fake",
                    // 	reloadButton: true
                    // });
                }
                if (location.hash.indexOf("mySchool") > -1) {
                    window.checks += 1;
                    if (window.checks === 4) {
                        window.done = true;
                        //   console.log("DONE");
                    }
                }
                //  console.log("ERROR CHECK", window.checks);
            },

            setDataErrorState: function (error) {
                if ((error) && (error.type != "NoConnection")) {
                    this.dataError = _.clone(error);
                    if (error.messagePath) {
                        this.dataError.message = Util.Data.getDataFromPath(this.toJSON(), error.messagePath);
                    }
                    this.trigger("dataError", this.dataError);
                } else {
                    delete this.dataError;
                }
            },

            catchErrors: function () {
                // flag and report errors
                this.on("sync", this.checkForErrorConditions.bind(this));
                this.on("error", this.onRequestError.bind(this));
            },

            onRequestError: function (model, errors /*, options*/ ) {
                // if we're here, it means that WebFOCUS has returned to us a
                // "NO EDA HTML Output" error, and we should react
                // appropriately.

                var errorMessage, error,
                    httpStatus = errors.status,
                    errorTypes = {
                        "NoData": {
                            type: "NoData",
                            httpStatus: errors.status,
                            header: "Error",
                            message: "We are experiencing a technical issue with our data servers. Please check back later.",
                            debugMessage: errorMessage,
                            reloadButton: true
                        },
                        "NoConnection": {
                            type: "NoConnection",
                            httpStatus: errors.status,
                            header: "Error",
                            message: "There is a technical issue with your connection to our data servers. Please check back later.",
                            debugMessage: errorMessage,
                            reloadButton: true
                        },
                        "ServerError": {
                            type: "ServerError",
                            httpStatus: errors.status,
                            header: "Error",
                            message: "There is a technical issue with our data servers. Please check back later.",
                            debugMessage: errorMessage,
                            reloadButton: true
                        }
                    };

                // apply any custom error objects given by the data model
                _.each(model.dataSourceInfo.customErrors, function (errorOverlay, errorType) {
                    _.extend(errorTypes[errorType], errorOverlay);
                });

                if (!httpStatus) {
                    // no http status means timeout or abort.
                    console.log("** Error with no HTTP status: statusText = " + errors.statusText);
                    console.log("** model:");
                    console.log(model);
                    console.log("** errors:");
                    console.log(errors);

                    setTimeout(function () {
                        $(window).resize();
                    }, 1500);

                    if (errors.statusText === "timeout" || errors.statusText === "error") {
                        // statusText of "error" is what we get when the network
                        // interface is down on Linux
                        error = errorTypes.NoConnection;
                    } else if (errors.statusText === "abort") {
                        // if statusText === "abort", we do not want to indicate an
                        // error. the user has just quickly changed filter criteria
                        // or something.
                        error = errorTypes.NoConnection;
                        //	return;
                    }
                } else if (httpStatus === 200) {
                    // status code okay, so there was something returned, but we
                    // didn't like it.
                    error = errorTypes.NoData;

                    if (errors.responseText) {
                        var titleMatch = errors.responseText.match(/<title>(.*?)<\/title>/i);
                        if (titleMatch && titleMatch.length > 1) {
                            error.debugMessage = titleMatch[1];
                        } else {
                            error.debugMessage = errors.responseText;
                        }
                    }
                } else if (httpStatus === 500 || httpStatus === 501) {
                    // something is really wrong with the server -- or,
                    // we gave it bad instructions, but if we did, let's keep that
                    // to ourselves and just blame the server. Yeehaw!
                    error = errorTypes.ServerError;
                } else if ((httpStatus >= 400 && httpStatus < 500) ||
                    (httpStatus >= 502 || httpStatus <= 505)) {
                    // connection was interrupted or something.
                    error = errorTypes.NoConnection;
                }

                // Right now this is just for errors that happen on the loading
                // screen.
                Util.globallyIndicateError(error);

                // set the data model to an errors state -- this is to allow the
                // rest of the app fine-grained control over how to respond to an
                // error.
                this.setDataErrorState(error);
            }
        };

        /**
         * Methods that go on both our Model and our DocumentModel, which doesn't
         * exist anymore, so...well, let's keep this for now, anyway.
         * @mixin
         */
        Mixins.ModelMixin = {

            /** constructor */
            initialize: function (attributes, options) {
                var setup = _({}).extend(options).value();

                this.dataSourceInfo = setup;
                this.loaded = false;

                // save bits of the setup we were passed
                if (setup.url) {
                    this.url = setup.url;
                }
                if (setup.defaultParams) {
                    this.setParams(setup.defaultParams || {});
                }

                this.catchErrors();
            },

            /** wrap original fetch to return a Promise instead of a Deferred */
            fetch: function () {
                var deferred = Backbone.Model.prototype.fetch.apply(this, arguments);
                return Promise.cast(deferred);
            },
            /** wrap original parse to support loaded flagging and optionally run
             * the results through a provided post-processor callback. */
            parse: function ( /*input*/ ) {
                var data = Backbone.Model.prototype.parse.apply(this, arguments);
                this.postProcess(data);
                this.loaded = true;
                return data;
            },

            // INFO methods

            /**
             * @returns {boolean} Whether a field is present in a row of this collection */
            hasField: function (fieldName) {
                return this.has(fieldName);
            },
            /**
             * @returns {Array} List of fields in each row of this collection */
            getFieldNames: function () {
                return _.keys(this.attributes);
            },
            /**
             * @returns {Boolean} Whether the data has been loaded yet or not */
            isLoaded: function () {
                return (this.loaded);
            },

            /**
             * @returns {String} a string that describes the fiscal years
             * this data is valid for */
            getYearString: function (yearFieldPath) {
                if (!yearFieldPath) {
                    yearFieldPath = this.getDefinition().yearPath;
                }
                return Util.Data.getDataFromPath(this.attributes, yearFieldPath) + "";
            },
            /**
             * @returns {String} a string that describes the fiscal years
             * this data is valid for */
            getLastYearString: function (lastYearFieldPath) {
                var lastYear;

                if (!lastYearFieldPath) {
                    lastYearFieldPath = this.getDefinition().lastYearPath;
                }
                if (lastYearFieldPath) {
                    lastYear = Util.Data.getDataFromPath(this.attributes, lastYearFieldPath);
                    return lastYear;
                } else {
                    // there' no last year available -- let's calculate one, if we can.
                    var year = parseInt(this.getYearString(), 10);
                    return (year - 1) + "";
                }
            }
        };

        /**
         * @mixin
         */
        Mixins.ReportsMixin = {
            /** find a report's model object, given either that model,
             * its numeric ID, or its url name */
            findReport: function (report) {
                if (_.isString(report)) {
                    var numericID = parseInt(report, 0);
                    if (_.isNaN(numericID)) {
                        // it's the "url" string identifier, cool.
                        return this.findWhere({
                            url: report
                        });
                    } else {
                        // it's the numeric id, let's run with it
                        return this.get(numericID);
                    }
                } else {
                    // I guess we were passed a report model! Freebie.
                    return report;
                }
            }
        };

        /**
         * @mixin
         */
        Mixins.FilterDataMixin = {
            /** return an array of built-out menu info objects in their default
             * state. This now just calls getCurrentMenus with an empty parameter
             * list (AKA no user selections), forcing it to barf out the default
             * menu values
             * @returns {Array} an array of the default built-out menu info
             * objects. */
            getDefaultMenus: function () {
                return this.getCurrentMenus({});
            },

            /** return an array of built-out menu info objects that's correct based
             * on current selections. it's separate from the recursive function
             * below so we have a clean place to patch its output.
             * @param {Object} a hash of the current state of the menu selections
             * @returns {Array} an array of built-out menu info objects that's
             * correct based on current selections */
            getCurrentMenus: function (currentSelections) {
                var menus = this.getCurrentMenusRecursor(currentSelections);
                if (this.patchMenuList) {
                    menus = this.patchMenuList(menus);
                }
                return menus;
            },

            /** apply any patches to the filter menus requested by the plugin.
             * @private
             * @param {Array} menuList an array of built-out menu info objects
             * @returns {Array} the menu list array that was passed in, but with all
             * patches applied */
            patchMenuList: function (menuList) {
                // there needs to be a real datasource with a real webFocusAdapter,
                // or else we have to bail
                if (!this.getDefinition() || !this.getDefinition().webFocusAdapter) {
                    return menuList;
                }
                var patchInfoArray = this.getDefinition().webFocusAdapter.filterPatches;

                _(patchInfoArray).each(function (patch) {
                    if (patch.type === "optionPrepend") {
                        // add an option to the top of a menu's options list
                        _(menuList).where({
                            "paramName": patch.fieldName
                        }).each(function (menu) {
                            menu.options.unshift(patch.option);
                        });
                    } else if (patch.type === "addHiddenField") {
                        menuList.push({
                            type: "hidden",
                            paramName: patch.fieldName,
                            value: patch.defaultValue
                        });
                    } else if (patch.type === "addAction") {
                        _(menuList).where({
                            "paramName": patch.fieldName
                        }).each(function (menu) {
                            if (!menu.actions) {
                                menu.actions = [];
                            }
                            menu.actions.push(patch.when);
                        });
                    }
                });

                return menuList;
            },

            /** return an array of built-out menu info objects that's correct based
             * on current selections. this does the actual work.
             * @private
             * @returns {Array} an array of built-out menu info objects that's
             * correct based on current selections
             * @param {Object} currentSelections the current set of parameters
             * generated by the config UI - this is how we know what's been selected
             * so far
             * @param {Object} thisMenu to be filled only by recursive calls; a menu
             * object from the decision tree
             * @param {Array} outputMenuList to be filled only by recursive calls;
             * an accumulator array that holds the result until it's time to return
             * it*/
            getCurrentMenusRecursor: function (currentSelections, thisMenu, outputMenuList) {
                var menuSelectionValue, selectedOption, dependentMenu;

                // we're going to iterate over each menu, and thisMenu will be
                // whatever we're looking at currently.  So if this is the initial
                // call, and not a subsequent recursive call, we must prime these
                // parameters with intial values.
                if (_.isUndefined(thisMenu)) {
                    thisMenu = this.toJSON();
                }
                if (!outputMenuList) {
                    outputMenuList = [];
                }

                // inputMenu will be falsy if we've encountered an option
                // with no dependent menus

                if (thisMenu) {
                    if (thisMenu.options && thisMenu.options.length) {
                        menuSelectionValue = currentSelections[thisMenu.paramName];
                        selectedOption = _(thisMenu.options).where({
                            code: menuSelectionValue
                        }).first();
                        // if there's a selected option shown by the parameters, get its
                        // dependent menu, if any -- if no selection, do that for the
                        // default
                        dependentMenu = (selectedOption ? selectedOption.dependentMenu : thisMenu.options[0].dependentMenu);

                        outputMenuList.push({
                            displayName: thisMenu.displayName,
                            paramName: thisMenu.paramName,
                            options: _.map(thisMenu.options, function (option) {
                                return _.pick(option, ["code", "displayName"]);
                            })
                        });
                        if (dependentMenu) {
                            this.getCurrentMenusRecursor(currentSelections, dependentMenu, outputMenuList);
                        }
                    } else if (thisMenu.meta.dataNotAvailableMessage) {
                        outputMenuList = [{
                            "paramName": "ECODEVREGION",
                            "displayName": "Region",
                            "options": [
                              {
                                "code": "FOC_NONE",
                                "displayName": thisMenu.meta.dataNotAvailableMessage
                              }
                            ]
                        }];
                    }

                }
                
                return outputMenuList;
            }
        };

        return Mixins;
    });
